/**
 * ClearChoice 2.0 Header Component
 */
'use client';

import { useEffect, useRef, useState } from 'react';

import { AppBar, Box } from '@mui/material';

import { useAppContext, useFacilityContext } from '@/context';
import { App } from '@/types/generated';

import type { IBanner } from '@/components/Banner/Banner';
import Banner from '@/components/Banner/Banner';
import HeaderDesktop from './HeaderDesktop';
import HeaderMobile from './HeaderMobile';
import { headerStyles } from './Header.styles';
import { useHeaderDrawer } from './hooks/useHeaderDrawer';
import { useScrolledFromTop } from './hooks/useScrolledFromTop';

interface IHeaderClearChoice2 {
	headerData?: App['header'] | null;
	logo?: App['logo'] | null;
	topAlertBanner?: IBanner;
	bottomAlertBanner?: IBanner;
	disableHeaderTopAlertBanner?: boolean | null;
	disableHeaderBottomAlertBanner?: boolean | null;
	hideNavigation?: boolean;
	/** Force-hide the "Schedule now" CTA. */
	hideSchedule?: boolean;
	/** Hide the search/find-a-center icon on mobile. */
	hideSearch?: boolean;
	showLanguageToggle?: boolean;
	logoHref?: string;
	/**
	 * When set, this number replaces the Contentful-sourced national/scheduling
	 * phone number in the header CTAs (desktop pill + mobile "Call us"), so a
	 * site-wide dynamic-number-insertion snippet (e.g. Patient Prism) can swap
	 * it client-side. Consumers gate this by their own experiment flag and pass
	 * the base number only under treatment. Facility/center numbers are never
	 * affected — the header only ever renders this national number.
	 */
	phoneSwapNumber?: string;
	/**
	 * TTB-322 experiment (`ghostPrimaryScheduleButton`). When true, the header's
	 * primary CTA renders as a lower-emphasis "ghost" button matching its
	 * neighbours: desktop "Schedule now" → tertiary variant, mobile "Call us" →
	 * secondary variant. Consumers gate this by their own VWO flag and pass the
	 * resolved boolean only under treatment.
	 */
	ghostPrimaryScheduleButton?: boolean;
}

const HeaderClearChoice2 = ({
	headerData,
	logo,
	topAlertBanner,
	bottomAlertBanner,
	disableHeaderTopAlertBanner = false,
	disableHeaderBottomAlertBanner = false,
	hideNavigation = false,
	hideSchedule = false,
	hideSearch = false,
	showLanguageToggle = false,
	logoHref: logoHrefProp,
	phoneSwapNumber,
	ghostPrimaryScheduleButton = false,
}: IHeaderClearChoice2) => {
	const { config } = useAppContext();
	const { officeInfo } = useFacilityContext();

	const [resolvedLogoHref, setResolvedLogoHref] = useState(logoHrefProp || '/');
	useEffect(() => {
		if (!logoHrefProp && globalThis.window !== undefined) {
			setResolvedLogoHref(globalThis.window.location.origin + '/');
		}
	}, [logoHrefProp]);

	const showTopAlertBannerMessagesDesktop =
		!disableHeaderTopAlertBanner && (topAlertBanner?.alertMessagesCollection?.items ?? []).length > 0;
	const showTopAlertBannerMessagesMobile =
		!disableHeaderTopAlertBanner && (topAlertBanner?.mobileAlertMessagesCollection?.items ?? []).length > 0;
	const showBottomAlertBannerMessagesDesktop =
		!disableHeaderBottomAlertBanner && (bottomAlertBanner?.alertMessagesCollection?.items ?? []).length > 0;
	const showBottomAlertBannerMessagesMobile =
		!disableHeaderBottomAlertBanner && (bottomAlertBanner?.mobileAlertMessagesCollection?.items ?? []).length > 0;

	// Mobile drawer state
	const {
		isDrawerOpen: isMobileDrawerOpen,
		openMenuDrawer: openMobileMenu,
		closeDrawer: closeMobileMenu,
	} = useHeaderDrawer();

	// Hide the top alert banner once the user scrolls away from the very top
	const isScrolled = useScrolledFromTop();

	// Measure AppBar height dynamically so the spacer always matches,
	// even when the top alert banner is present and changes the total height.
	const appBarRef = useRef<HTMLDivElement>(null);
	const [appBarHeight, setAppBarHeight] = useState<number>(0);

	useEffect(() => {
		const el = appBarRef.current;
		if (!el) return;

		const syncHeight = () => setAppBarHeight(el.offsetHeight);
		syncHeight();

		if (typeof ResizeObserver !== 'undefined') {
			const observer = new ResizeObserver(syncHeight);
			observer.observe(el);
			return () => observer.disconnect();
		}

		globalThis.addEventListener('resize', syncHeight);
		return () => globalThis.removeEventListener('resize', syncHeight);
	}, [isScrolled]);

	// Track facility set event
	useEffect(() => {
		if (officeInfo?.code) {
			void import('@/utils/dataLayerConfig')
				.then(({ dataLayerConfig }) => {
					dataLayerConfig({
						event: 'facility_set',
						brand: config.name,
						website_selected_facility_id: officeInfo?.code,
						website_selected_facility_name: officeInfo?.name,
					});
					return true;
				})
				.catch(console.error);
		}
	}, [officeInfo?.code, config.name, officeInfo?.name]);

	return (
		<>
			<AppBar
				ref={appBarRef}
				position="fixed"
				sx={headerStyles.headerAppBar}
				data-testid="section_header_clearchoice2"
			>
				{/* Top Alert Banner — inside AppBar so it's part of the fixed header.
				    Hidden once the user scrolls down so only the nav bar remains pinned. */}
				{!isScrolled && (showTopAlertBannerMessagesDesktop || showTopAlertBannerMessagesMobile) && (
					<Banner
						dataTestId="header_top_alert_banner"
						variant={'Alert Banner'}
						alertMessagesCollection={
							showTopAlertBannerMessagesDesktop ? topAlertBanner?.alertMessagesCollection : undefined
						}
						mobileAlertMessagesCollection={
							showTopAlertBannerMessagesMobile ? topAlertBanner?.mobileAlertMessagesCollection : undefined
						}
					/>
				)}

				{/* Desktop Header — full-width stacked navigation bar */}
				<Box sx={{ display: { xs: 'none', md: 'block' } }}>
					<Box sx={headerStyles.desktopHeaderContainer}>
						<HeaderDesktop
							headerData={headerData}
							logo={logo}
							hideNavigation={hideNavigation}
							hideSchedule={hideSchedule}
							showLanguageToggle={showLanguageToggle}
							logoHref={resolvedLogoHref}
							phoneSwapNumber={phoneSwapNumber}
							ghostPrimaryScheduleButton={ghostPrimaryScheduleButton}
						/>
					</Box>
				</Box>

				{/* Mobile Header */}
				<Box sx={{ display: { xs: 'block', md: 'none' } }}>
					<HeaderMobile
						headerData={headerData}
						logo={logo}
						isDrawerOpen={isMobileDrawerOpen}
						onMenuClick={openMobileMenu}
						onClose={closeMobileMenu}
						hideNavigation={hideNavigation}
						hideSchedule={hideSchedule}
						hideSearch={hideSearch}
						showLanguageToggle={showLanguageToggle}
						logoHref={resolvedLogoHref}
						phoneSwapNumber={phoneSwapNumber}
						ghostPrimaryScheduleButton={ghostPrimaryScheduleButton}
					/>
				</Box>
			</AppBar>
			{/* Spacer: height matches the actual fixed AppBar height (including any alert banner) */}
			<Box
				aria-hidden
				sx={{
					width: '100%',
					flexShrink: 0,
					height: appBarHeight > 0 ? `${appBarHeight}px` : { xs: '48px', md: '96px' },
				}}
			/>
			{/* Bottom Alert Banner — outside AppBar so it scrolls away with the page */}
			{(showBottomAlertBannerMessagesDesktop || showBottomAlertBannerMessagesMobile) && (
				<Banner
					dataTestId="header_bottom_alert_banner"
					variant={'Alert Banner'}
					alertMessagesCollection={
						showBottomAlertBannerMessagesDesktop ? bottomAlertBanner?.alertMessagesCollection : undefined
					}
					mobileAlertMessagesCollection={
						showBottomAlertBannerMessagesMobile
							? bottomAlertBanner?.mobileAlertMessagesCollection
							: undefined
					}
				/>
			)}
		</>
	);
};

export default HeaderClearChoice2;
